import { useStore } from 'vuex';

import { LineItem, LineItemProperty, Package, PackageProperty, Plan, PlanProperty } from '@/common/models';
import LineItemPropertyKeys from '@/common/models/lineitem/LineItemPropertyKeys.enum';
import EntityPropertyUpdateHandler from '@/org-app/services/entityPropertyHandlers/EntityPropertyUpdateHandler';

interface IEntityExternalDealsByMediaOwnerUpdate {
  dealIDsByMediaOwnerID: Record<number, string[]>;
  dealIDs: string[];
}

interface IEntityExternalDealsBySSPUpdate {
  dealIDsBySspID: Record<number, string[]>;
  dealIDs: string[];
}

const entityPropertyModels = {
  Plan: PlanProperty,
  LineItem: LineItemProperty,
  Package: PackageProperty,
};

export default class EntityPropertyDealUpdateHandler extends EntityPropertyUpdateHandler {
  constructor(entity: LineItem | Plan | Package, store = useStore()) {
    super(entity, store);
  }

  updateEntityDealID(dealID: string): Promise<void> {
    const dealProperty = new entityPropertyModels[this.entity.type](null, null, LineItemPropertyKeys.DEAL_ID, dealID);
    return this.updateEntityProperties([dealProperty]);
  }

  removeEntityDealID(): Promise<void> {
    return this.removeEntityProperties([LineItemPropertyKeys.DEAL_ID]);
  }

  updateEntityDealIDSSP(dealIDSSP: string): Promise<void> {
    const dealProperty = new entityPropertyModels[this.entity.type](
      null,
      null,
      LineItemPropertyKeys.DEAL_ID_SSP,
      dealIDSSP,
    );
    return this.updateEntityProperties([dealProperty]);
  }

  removeEntityDeals(): Promise<void> {
    return this.removeEntityProperties([
      LineItemPropertyKeys.DEAL_IDS_BY_MEDIAOWNER,
      LineItemPropertyKeys.DEAL_IDS,
      LineItemPropertyKeys.DEAL_ID_SSP,
      LineItemPropertyKeys.DEAL_IDS_BY_SSP,
    ]);
  }

  updateBuyFromDealsEnabled(value: boolean): Promise<void> {
    // Since buy from deals is not applicable to packages, we return null
    if (this.entity.type === 'Package') return Promise.resolve();
    const action = `update${this.entity.type}BuyFromDealsEnabled`;
    return this.store.dispatch(action, {
      [this.entityType]: this.entity,
      value,
    });
  }

  updateEntityDealsByMediaOwner(dealUpdate: IEntityExternalDealsByMediaOwnerUpdate) {
    return this.updateEntityProperties([
      new entityPropertyModels[this.entity.type](
        null,
        null,
        LineItemPropertyKeys.DEAL_IDS_BY_MEDIAOWNER,
        JSON.stringify(dealUpdate.dealIDsByMediaOwnerID),
        false,
      ),
      new entityPropertyModels[this.entity.type](
        null,
        null,
        LineItemPropertyKeys.DEAL_IDS,
        dealUpdate.dealIDs.join(','),
        false,
      ),
    ]);
  }

  updateEntityDealsBySSP(dealUpdate: IEntityExternalDealsBySSPUpdate) {
    return this.updateEntityProperties([
      new entityPropertyModels[this.entity.type](
        null,
        null,
        LineItemPropertyKeys.DEAL_IDS_BY_SSP,
        JSON.stringify(dealUpdate.dealIDsBySspID),
        false,
      ),
    ]);
  }
}
